import { Address, IOlavEntry } from '../address/address.model';
import {
  CommonMappingData,
  IAktionen,
  IBankDetails,
  IBulletConfirmationStatusChangeResponse,
  IDifferentInvoiceRecipient,
  IERechnungsInfo,
  IILAdresse,
  ITarif,
} from '../integrationlayer/api.model';
import { IdCard, IOrderDetailsInfo, ORDER_DETAILS_VERSION, toReferrencedFiles } from '../order/order.model';
import { IFileReferenceModel, IUploadResult } from '../upload/file.model';
import { PreisIndikation, TariffSelectionCardData } from '../availabilities/availabilities.model';
import { CommonPDFLinks } from '../pdf-links/pdf-links.model';
import { PriceSheet } from '../price-sheet/price-sheet.model';

export type IRelocationConfirmationStatusResponse = IBulletConfirmationStatusChangeResponse;

export type RelocationConfirmationStatusResponse = IBulletConfirmationStatusChangeResponse;

export type RelocationPDFLinks = CommonPDFLinks;

export type RelocationDetails = IOrderDetailsInfo & { email: string };

export type IRelocationMappingData = CommonMappingData & { zustelladresse: '' | 'from' | 'to' };

export interface IRelocationDataWrapper {
  addressGroupId: number;
  changeData: string; // IRelocationDataDTO
  collaborationMode: string;
  correlationId: string;
  errorCode: number;
  id: number;
  modifiedAt: string;
  processStatus: string;
  responseData: string; // ...
  retryAGI: string;
  retryAllowed: boolean;
  status: string;
  userId: string;
  cancelled: boolean;
  version: 'V1' | 'V2'; // TODO: 'V1' seems to be always undefined
}

export interface IRelocationData {
  relocationFromAddress: Address;
  relocationToOlav: IOlavEntry;
  relocationToAddress: Address;
  registrationDate: string;
  deregistrationDate: string;
  relocationDetails: IOrderDetailsInfo[];
  idCard?: IdCard;
  uploadedFiles: IUploadResult;
  useSubstituteDetails: boolean;
  pdfLinks: RelocationPDFLinks;
  noOnlineCategories?: string[];
  selectedTariffs?: TariffSelectionCardData[];
  eRechnungChangeIndices?: number[];
  sepaChangeIndices?: number[];
}

// TODO: This DTO is used for /confirmStatus. Meaning, both V1 and V2 relocation always get mapped to a DTO based on V1.
export interface IRelocationDataDTO {
  selectedOlav?: IOlavEntry;
  oldAddress?: Address;
  newAddress?: Address;
  deregistrationDate?: string;
  registrationDate?: string;
  relocationSubmittedAt?: string;
  orderDetailsInfo?: IOrderDetailsInfo[];
  idcard?: IdCard;
  referencedFiles?: IFileReferenceModel[];
  useSubstituteDetails?: boolean;
  pdfLinks?: RelocationPDFLinks;
  noOnlineCategories?: string[];
  noLongerAvailableCategories?: string[];
  priceSheets?: PriceSheet[];
  version?: string;
  selectedTariffs?: TariffSelectionCardData[];
  eRechnungsInfo?: IERechnungsInfo;
  eRechnungChangeIndices?: number[];
  sepaChangeIndices?: number[];
  voucherInfo?: IAktionen;
  cancelled?: boolean;
  differentInvoiceRecipient?: IDifferentInvoiceRecipient; // TODO: Address vs. ICustomerAdressDetails differences
}

// TODO: This DTO is only used for /confirm.
export interface IRelocationDTOV2 {
  oldAddress?: Address;
  newAddressOlav?: IOlavEntry;
  registrationDate?: string;
  deregistrationDate?: string;
  idCard?: IdCard;
  referencedFiles?: IFileReferenceModel[];
  useSubstituteDetails?: boolean; // never set
  pdfLinks?: RelocationPDFLinks;
  grantedMarketingPermission?: boolean;
  priceIndications?: PreisIndikation[];
  noOnlineCategories?: string[];
  noLongerAvailableCategories?: string[];
  selectedTariffs?: TariffSelectionCardData[];
  version?: string;
  priceSheets?: PriceSheet[];
  voucherInfo?: IAktionen;
  mappingData?: IRelocationMappingData[];
  sepaMandat?: IBankDetails;
  eRechnungsInfo?: IERechnungsInfo;
  rechnungsAdresse?: IILAdresse; // never set
  addressCode?: string; // never set
  differentInvoiceRecipient?: IDifferentInvoiceRecipient; // TODO: Address vs. ICustomerAdressDetails differences
  // TODO: Attributs ignored in /confirm.
  id?: number;
  orderedAt?: string;
  processStatus?: string;
  invoiceAddressOlav?: IOlavEntry;
}

export const newRelocationDTOV1 = (relocationData: IRelocationData): IRelocationDataDTO => {
  const dto: IRelocationDataDTO = {
    selectedOlav: relocationData?.relocationToOlav,
    oldAddress: relocationData?.relocationFromAddress,
    newAddress: relocationData?.relocationToAddress,
    deregistrationDate: relocationData?.deregistrationDate,
    registrationDate: relocationData?.registrationDate,
    orderDetailsInfo: relocationData?.relocationDetails,
    idcard: relocationData?.idCard,
    referencedFiles: relocationData?.uploadedFiles ? toReferrencedFiles(relocationData?.uploadedFiles.uploadedFiles) : null,
    useSubstituteDetails: relocationData?.useSubstituteDetails,
    pdfLinks: relocationData?.pdfLinks,
    noOnlineCategories: relocationData?.noOnlineCategories,
    selectedTariffs: relocationData?.selectedTariffs,
    eRechnungChangeIndices: relocationData?.eRechnungChangeIndices,
    sepaChangeIndices: relocationData?.sepaChangeIndices,
  };

  return {
    ...dto,
    orderDetailsInfo: relocationData?.relocationDetails.map(d => {
      if (!d.partnerDetails?.physischePerson?.geburtsdatum?.includes('T00:00Z')) return d;

      return {
        ...d,
        partnerDetails: {
          ...d.partnerDetails,
          physischePerson: {
            ...d.partnerDetails.physischePerson,
            geburtsdatum: d.partnerDetails.physischePerson.geburtsdatum.replace('T00:00Z-', ''),
          },
        },
      };
    }),
  };
};

export const newRelocationDetailsInfo = (
  relocationStatus: 'Abmeldung' | 'Anmeldung',
  _zustelladresse: 'from' | 'to' | '',
  _category: string,
  _customerNumber: string,
  _accountNUmber: string,
  _tariff: ITarif,
  _availabilityDetailsFernwaerme: string[],
): IOrderDetailsInfo => {
  return {
    apiVersion: ORDER_DETAILS_VERSION,
    produktDetails: null,
    invoiceAddress: null,
    eRechnungInfo: null,
    bezahlartIcon: null,
    bezahlartText: null,
    status: relocationStatus,
    zustelladresse: _zustelladresse,
    category: _category,
    customerNumber: _customerNumber,
    accountNumber: _accountNUmber,
    physischePerson: null,
    tariff: _tariff,
    availabilityDetailsFernwaerme: _availabilityDetailsFernwaerme,
  };
};

export interface OSSRelocationData {
  customer: OssCustomer;
  address: OSSAddress;
}

export interface OssCustomerIdentification {
  birthDate: string;
  oldAddress: OSSAddress;
}

export interface OssCustomer {
  salutation: string;
  firstName: string;
  lastName: string;
  email: string;
  identification: OssCustomerIdentification;
}

export interface OSSAddress {
  streetName: string;
  houseNumber: string;
  houseNumberExtension?: string;
  postalCode: string;
  cityName: string;
  isoCountryCode: string;
  additionalAddressLines?: string[];
}

export interface OssRelocationResponse {
  id: string;
  iamUserId: string;
  customer: OssCustomer;
  address: OSSAddress;
  createdAt: string;
}
